import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Chapter } from "../components/Reveal";
import { Counter } from "../components/Counter";
import { CtaSection } from "./Home";
import { SiteImg } from "../hooks/useSiteImages";
import { T } from "../hooks/useSiteTexts";
import { IMAGES } from "../data/content";

const CHAPTERS = [
    {
        num: "01",
        title: "Uma empresa familiar",
        text: "A Transportes Alzira, Lda. nasceu em 1998, na Sertã, como uma empresa familiar com um propósito claro: fazer bem, cumprir sempre e tratar cada cliente de forma próxima. Esses princípios continuam a orientar tudo o que fazemos.",
        image: IMAGES.team,
    },
    {
        num: "02",
        title: "Crescimento sustentado",
        text: "Ao longo de mais de duas décadas, investimos continuamente na frota e na equipa. De uma operação local, crescemos para uma estrutura com mais de 60 camiões e 80 reboques — basculantes, cisternas, tratores, porta-máquinas e semirreboques especializados.",
        image: IMAGES.scania,
    },
    {
        num: "03",
        title: "Capacidade para grandes obras",
        text: "Hoje, somos um parceiro de confiança em obras e projetos de grande dimensão em todo o território nacional, com capacidade para operações internacionais sempre que necessário. A nossa capacidade operacional permite-nos responder a operações exigentes, com rigor, segurança e rapidez.",
        image: IMAGES.duskTruck,
    },
];

const TIMELINE = [
    { year: "1998", text: "Fundação da Transportes Alzira, Lda. na Sertã." },
    { year: "2005", text: "Consolidação da frota de basculantes e início de operações continuadas em obra." },
    { year: "2012", text: "Alargamento da atividade ao transporte de betuminosos e água." },
    { year: "2018", text: "Primeiras operações internacionais, mantendo Portugal como mercado principal." },
    { year: "Hoje", text: "Mais de 60 camiões e 80 reboques, e uma equipa experiente, pronta para qualquer desafio." },
];

const VALUES = [
    { title: "Segurança", desc: "A segurança de pessoas, cargas e operações está sempre em primeiro lugar." },
    { title: "Compromisso", desc: "Cumprimos o que prometemos — prazos, quantidades e qualidade." },
    { title: "Profissionalismo", desc: "Equipa experiente, frota cuidada e processos rigorosos." },
    { title: "Proximidade", desc: "Empresa familiar: cada cliente é acompanhado de perto, do início ao fim." },
];

export default function Empresa() {
    return (
        <>
            <PageHero
                overline="A Empresa"
                title="Transportes Alzira."
                accent="Experiência que move o futuro."
                subtitle={<T k="empresa-hero-sub" fallback="Empresa familiar fundada em 1998, especializada em transporte rodoviário de mercadorias e soluções de transporte pesado." />}
                image={IMAGES.duskTruck}
                slot="empresa-hero"
            />

            <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="empresa-chapters">
                {CHAPTERS.map((c, i) => (
                    <div
                        key={c.num}
                        className={`grid items-center gap-12 py-16 lg:grid-cols-2 lg:gap-20 ${
                            i > 0 ? "border-t border-white/10" : ""
                        }`}
                    >
                        <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                            <Chapter num={c.num} label="A nossa história" />
                            <h2 className="mt-8 font-display text-3xl font-black uppercase leading-tight tracking-tight sm:text-4xl">
                                {c.title}
                            </h2>
                            <p className="mt-6 max-w-lg text-sm leading-relaxed text-zinc-400 md:text-base">
                                {c.text}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15} className={i % 2 === 1 ? "lg:order-1" : ""}>
                            <div className="group overflow-hidden border border-white/10">
                                <SiteImg
                                    slot={`empresa-${i + 1}`}
                                    fallback={c.image}
                                    alt={c.title}
                                    loading="lazy"
                                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                        </Reveal>
                    </div>
                ))}
            </section>

            <section className="border-y border-white/10 bg-coal" data-testid="empresa-timeline">
                <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
                    <Reveal>
                        <Chapter num={4} label="Percurso" />
                        <h2 className="mt-8 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">
                            De 1998 até <span className="text-brand">hoje.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-5">
                        {TIMELINE.map((t, i) => (
                            <Reveal key={t.year} delay={i * 0.08} className="bg-coal">
                                <div className="h-full p-8" data-testid={`timeline-${i}`}>
                                    <p className="font-display text-3xl font-black text-brand">
                                        {t.year}
                                    </p>
                                    <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                                        {t.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-16 grid grid-cols-2 gap-10 lg:grid-cols-5">
                        {[
                            { to: 60, suffix: "+", label: "Camiões" },
                            { to: 80, suffix: "+", label: "Reboques" },
                            { to: 25, suffix: "+", label: "Anos de experiência" },
                            { to: 0, suffix: "", label: "Mercado principal", text: "PT" },
                            { to: 6, suffix: "", label: "Áreas de serviço" },
                        ].map((s, i) => (
                            <Reveal key={s.label} delay={i * 0.08}>
                                <p className="font-display text-5xl font-black">
                                    {s.text ? (
                                        <span className="text-brand">{s.text}</span>
                                    ) : (
                                        <Counter to={s.to} suffix={s.suffix} />
                                    )}
                                </p>
                                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    {s.label}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="empresa-values">
                <Reveal>
                    <Chapter num={5} label="Os nossos valores" />
                    <h2 className="mt-8 max-w-2xl font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">
                        O que nos <span className="text-brand">define.</span>
                    </h2>
                </Reveal>
                <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.title} delay={i * 0.08}>
                            <div
                                className="group h-full border border-white/10 bg-coal p-8 transition-colors duration-500 hover:border-brand"
                                data-testid={`value-${i}`}
                            >
                                <span className="font-mono text-sm font-bold text-brand">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <h3 className="mt-4 font-display text-2xl font-black uppercase tracking-tight">
                                    {v.title}
                                </h3>
                                <p className="mt-3 text-sm text-zinc-400">{v.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <div className="mt-20 flex flex-col items-start justify-between gap-8 border border-white/10 bg-steel p-10 lg:flex-row lg:items-center lg:p-14">
                        <div>
                            <h3 className="font-display text-3xl font-black uppercase tracking-tight">
                                Conheça a nossa frota
                            </h3>
                            <p className="mt-3 max-w-md text-sm text-zinc-400">
                                Mais de 60 camiões e 80 reboques, além de
                                equipamentos especializados, prontos para a sua
                                operação.
                            </p>
                        </div>
                        <Link
                            to="/frota"
                            data-testid="empresa-fleet-button"
                            className="group inline-flex items-center gap-2 bg-brand px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink"
                        >
                            Ver frota
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </div>
                </Reveal>
            </section>

            <CtaSection />
        </>
    );
}
